import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, throwError, timer } from 'rxjs';
import { map, switchMap, catchError, mergeMap, retry, delay, filter } from 'rxjs/operators';

import * as taskActions from '../actions/task.actions';
import * as fromServices from '../../services';

@Injectable()
export class TaskEffects {
  constructor(
    private actions$: Actions,
    private taskService: fromServices.TaskService,
  ) {}

  loadAllOpenTasks$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(taskActions.LOAD_ALL_OPEN_TASKS),
      switchMap((payload: any) => {
        return this.taskService.getAllOpen().pipe(
          map(response => new taskActions.LoadAllOpenTasksSuccess(response)),
          catchError(error => of(new taskActions.LoadAllOpenTasksFail(error))),
        );
      }),
    );
  });

  loadOpenTasks$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(taskActions.LOAD_OPEN_TASKS),
      map((action: taskActions.LoadOpenTasks) => action.payload),
      switchMap(backendUrl => {
        return this.taskService.getOpen(backendUrl).pipe(
          map(response => new taskActions.LoadOpenTasksSuccess(response, backendUrl)),
          catchError(error => of(new taskActions.LoadOpenTasksFail(error))),
        );
      }),
    );
  });

  loadOpenTask$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(taskActions.LOAD_OPEN_TASK),
      map((action: taskActions.LoadOpenTask) => action.payload),
      // delay(1500),
      switchMap(({ backendUrl, taskId, eventName, wasMyTask }) => {
        return this.taskService.getTask(backendUrl, taskId).pipe(
          mergeMap(response => (response?.id ? of(response) : throwError(() => 'empty'))),
          retry({ count: 3, delay: error => (error?.status === 403 ? throwError(() => error) : timer(1000)) }),
          map(response => new taskActions.LoadOpenTaskSuccess(response, backendUrl, eventName, wasMyTask)),
          catchError(error => of(new taskActions.LoadOpenTaskFail(error))),
        );
      }),
    );
  });

  loadMyCompletedTasks$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(taskActions.LOAD_MY_COMPLETED_TASKS),
      switchMap((payload: any) => {
        return this.taskService.getAllMyCompleted().pipe(
          map(response => new taskActions.LoadMyCompletedTasksSuccess(response)),
          catchError(error => of(new taskActions.LoadMyCompletedTasksFail(error))),
        );
      }),
    );
  });

  loadGroupCompletedTasks$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(taskActions.LOAD_GROUP_COMPLETED_TASKS),
      switchMap((payload: any) => {
        return this.taskService.getAllGroupCompleted().pipe(
          map(response => new taskActions.LoadGroupCompletedTasksSuccess(response)),
          catchError(error => of(new taskActions.LoadGroupCompletedTasksFail(error))),
        );
      }),
    );
  });

  adjustTaskTotalsOnAssignment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(taskActions.LOAD_OPEN_TASK_SUCCESS),
      filter((action: taskActions.LoadOpenTaskSuccess) => action.eventName === 'assignment'),
      map((action: taskActions.LoadOpenTaskSuccess) => {
        const wasMyTask = action.wasMyTask ?? false;
        const taskOwner = action.payload.taskOwner;
        return new taskActions.AdjustTaskTotals({
          openMy: (wasMyTask ? -1 : 0) + (taskOwner ? 1 : 0),
          openGroup: 0,
          completedMy: 0,
          completedGroup: 0,
        });
      }),
    );
  });

  adjustTaskTotalsOnCreate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(taskActions.LOAD_OPEN_TASK_SUCCESS),
      filter((action: taskActions.LoadOpenTaskSuccess) => action.eventName === 'create'),
      map((action: taskActions.LoadOpenTaskSuccess) => action.payload),
      map(
        response =>
          new taskActions.AdjustTaskTotals({
            openMy: response.taskOwner ? 1 : 0,
            openGroup: 1,
            completedMy: 0,
            completedGroup: 0,
          }),
      ),
    );
  });

  loadTaskTotals$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(taskActions.LOAD_TASK_TOTALS),
      switchMap((payload: any) => {
        return this.taskService.getTaskTotals().pipe(
          map(response => new taskActions.LoadTaskTotalsSuccess(response)),
          catchError(error => of(new taskActions.LoadTaskTotalsFail(error))),
        );
      }),
    );
  });
}
